/**
 * Identity Verification Page
 * ==========================
 * Multi-step verification flow for new members.
 * Steps:
 * 1. Email verification
 * 2. Phone verification
 * 3. Government ID verification (Stripe Identity)
 * 4. Legal disclosures acceptance
 * 
 * Age Restrictions:
 * - Under 18: Account denied after ID verification
 * - Under 21: Alcohol features blocked, age must be visible
 * 
 * All steps must be completed before profile goes live.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Mail, Phone, FileText, Check, ChevronRight, 
  AlertCircle, Shield, Loader2, RefreshCw, ExternalLink,
  XCircle, AlertTriangle
} from 'lucide-react';
import NearULogo from '../components/NearULogo';
import LegalDisclosuresStep from '../components/LegalDisclosuresStep';
import useTheme from '../hooks/useTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get user ID from localStorage or generate temp one
const getUserId = () => {
  let userId = localStorage.getItem('nearU_userId');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('nearU_userId', userId);
  }
  return userId;
};

const VerificationFlowPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [step, setStep] = useState(1); // 1: email, 2: phone, 3: ID, 4: legal
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [userId] = useState(getUserId());
  
  // Form fields
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState('');
  
  // Age restriction state
  const [isAgeRestricted, setIsAgeRestricted] = useState(false);
  const [age, setAge] = useState(null);
  const [isUnder21, setIsUnder21] = useState(false);
  
  // Verification attempts tracking
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [isVerificationLocked, setIsVerificationLocked] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${userId}` }
  });
  
  // Auto-save progress to localStorage
  const saveProgress = useCallback(() => {
    const progress = {
      step,
      email,
      phone,
      userId,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem('nearU_verification_progress', JSON.stringify(progress));
  }, [step, email, phone, userId]);
  
  // Restore progress from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('nearU_verification_progress');
    if (saved) {
      try {
        const progress = JSON.parse(saved);
        if (progress.userId === userId) {
          if (progress.email) setEmail(progress.email);
          if (progress.phone) setPhone(progress.phone);
        }
      } catch (e) {
        console.error('Error restoring progress:', e);
      }
    }
  }, [userId]);
  
  // Save progress when fields change
  useEffect(() => {
    if (email || phone) {
      saveProgress();
    }
  }, [email, phone, step, saveProgress]);

  const fetchStatus = useCallback(async () => {
    try {
      // Use authenticated endpoint to get full status including verification attempts
      const token = localStorage.getItem('nearU_auth_token') || sessionStorage.getItem('nearU_auth_token');
      
      const response = await axios.get(
        `${API}/identity/status`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const data = response.data;
      setStatus(data);
      setAge(data.age);
      setIsUnder21(data.is_under_21);
      
      // Track verification attempts
      setVerificationAttempts(data.verification_attempts || 0);
      setAttemptsRemaining(data.verification_attempts_remaining ?? 3);
      setIsVerificationLocked(data.verification_locked || false);
      
      // Check for age restriction
      if (data.id_verification?.status === 'age_restricted') {
        setIsAgeRestricted(true);
        setStep(3); // Keep on ID step to show error
        return;
      }
      
      // Determine current step based on verification status
      if (!data.email_verification?.verified) {
        setStep(1);
        setEmail(data.email_verification?.email || '');
      } else if (!data.phone_verification?.verified) {
        setStep(2);
        setPhone(data.phone_verification?.phone || '');
      } else if (data.id_verification?.status !== 'verified') {
        setStep(3);
      } else if (!data.legal_disclosures?.accepted) {
        setStep(4);
      } else if (data.profile_active) {
        // All verified - redirect to home
        navigate('/explore');
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate, userId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSendEmailCode = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/identity/send-email-code`,
        { email },
        getAuthHeaders()
      );
      setCodeSent(true);
      if (response.data.dev_code) {
        setDevCode(response.data.dev_code);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!phone || phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/identity/send-phone-code`,
        { phone },
        getAuthHeaders()
      );
      setCodeSent(true);
      if (response.data.dev_code) {
        setDevCode(response.data.dev_code);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      await axios.post(
        `${API}/identity/verify-code`,
        {
          code: verificationCode,
          verification_type: step === 1 ? 'email' : 'phone'
        },
        getAuthHeaders()
      );
      
      setCodeSent(false);
      setVerificationCode('');
      setDevCode('');
      
      if (step === 1) {
        setStep(2);
      } else if (step === 2) {
        setStep(3);
      }
      
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartIdVerification = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/identity/create-session`,
        { return_url: `${window.location.origin}/verification-complete` },
        getAuthHeaders()
      );
      
      if (response.data.already_verified) {
        await fetchStatus();
        return;
      }
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start verification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLegalDisclosuresComplete = async () => {
    await fetchStatus();
  };

  // Theme-aware colors
  const colors = {
    background: isDark ? '#1A0F0A' : '#FDFCFA',
    cardBg: isDark ? 'rgba(215, 181, 109, 0.08)' : 'rgba(139, 105, 20, 0.05)',
    inputBg: isDark ? 'rgba(26, 15, 10, 0.8)' : '#FFFFFF',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#A57C33' : '#6D6358',
    textAccent: isDark ? '#D7B56D' : '#8B6914',
    border: isDark ? 'rgba(165, 124, 51, 0.3)' : 'rgba(139, 105, 20, 0.25)',
    goldGradient: isDark 
      ? 'linear-gradient(135deg, #D7B56D, #A57C33)' 
      : 'linear-gradient(135deg, #A57C33, #8B6914)',
    inactiveBg: isDark ? 'rgba(165, 124, 51, 0.3)' : 'rgba(139, 105, 20, 0.2)',
    inactiveText: isDark ? '#6B5B4D' : '#9A9489',
    infoBg: isDark ? 'rgba(26, 15, 10, 0.6)' : 'rgba(139, 105, 20, 0.06)'
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Loader2 size={32} className="animate-spin" style={{ color: colors.textAccent }} />
      </div>
    );
  }

  // Age Restricted Screen - Under 18
  if (isAgeRestricted) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <XCircle size={40} color="#EF4444" />
          </div>
          
          <h1 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '24px',
            color: colors.text,
            marginBottom: '16px'
          }}>
            Age Verification Failed
          </h1>
          
          <p style={{
            color: colors.textMuted,
            fontSize: '16px',
            lineHeight: '1.6',
            marginBottom: '24px'
          }}>
            NearU is only available to users who are 18 years of age or older.
            Based on your verified identification, we are unable to create your account.
          </p>
          
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              justifyContent: 'center'
            }}>
              <AlertTriangle size={20} color="#EF4444" />
              <span style={{ color: '#EF4444', fontSize: '14px', fontWeight: '600' }}>
                You must be 18 or older to join NearU
              </span>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '14px 32px',
              background: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(139, 105, 20, 0.15)',
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              color: colors.textAccent,
              fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Verification Locked Screen - Max Attempts Reached
  if (isVerificationLocked) {
    return (
      <div 
        data-testid="verification-locked-screen"
        style={{
          minHeight: '100vh',
          background: colors.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        <div style={{
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(245, 158, 11, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <AlertTriangle size={40} color="#F59E0B" />
          </div>
          
          <h1 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '24px',
            color: colors.text,
            marginBottom: '16px'
          }}>
            Verification Locked
          </h1>
          
          <p style={{
            color: colors.textMuted,
            fontSize: '16px',
            lineHeight: '1.6',
            marginBottom: '24px'
          }}>
            You have reached the maximum number of verification attempts (3).
            For your security, your account has been locked from further verification attempts.
          </p>
          
          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              gap: '12px'
            }}>
              <span style={{ color: '#F59E0B', fontSize: '14px', fontWeight: '600' }}>
                To unlock your account, please contact:
              </span>
              <a 
                href="mailto:info@nearuapp.net"
                style={{ 
                  color: colors.textAccent, 
                  fontSize: '16px', 
                  fontWeight: '700',
                  textDecoration: 'none'
                }}
              >
                info@nearuapp.net
              </a>
            </div>
          </div>
          
          <p style={{
            color: colors.inactiveText,
            fontSize: '13px',
            lineHeight: '1.5',
            marginBottom: '24px'
          }}>
            Our support team will review your account and assist you with completing your verification.
            Please have your government-issued ID ready for manual review.
          </p>
          
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '14px 32px',
              background: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(139, 105, 20, 0.15)',
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              color: colors.textAccent,
              fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  const stepConfig = [
    { step: 1, label: 'Email', icon: Mail, completed: status?.email_verification?.verified },
    { step: 2, label: 'Phone', icon: Phone, completed: status?.phone_verification?.verified },
    { step: 3, label: 'ID', icon: FileText, completed: status?.id_verification?.status === 'verified' },
    { step: 4, label: 'Legal', icon: Shield, completed: status?.legal_disclosures?.accepted }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.background,
      paddingBottom: '40px'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: `1px solid ${colors.border}`,
        textAlign: 'center',
        background: isDark ? 'transparent' : '#F5F2ED'
      }}>
        <NearULogo size="small" showText={false} />
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '24px',
          color: colors.text,
          marginTop: '16px',
          marginBottom: '8px'
        }}>
          Verify Your Identity
        </h1>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px'
        }}>
          Complete verification to activate your profile
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '6px',
        padding: '24px 16px',
        borderBottom: `1px solid ${isDark ? 'rgba(165, 124, 51, 0.1)' : 'rgba(139, 105, 20, 0.1)'}`
      }}>
        {stepConfig.map((s, idx) => {
          const Icon = s.icon;
          const isActive = step === s.step;
          const isCompleted = s.completed;
          
          return (
            <React.Fragment key={s.step}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: isCompleted 
                    ? 'linear-gradient(135deg, #4CAF50, #2E7D32)'
                    : isActive 
                      ? colors.goldGradient
                      : isDark ? 'rgba(165, 124, 51, 0.1)' : 'rgba(139, 105, 20, 0.1)',
                  border: isActive && !isCompleted ? `2px solid ${colors.textAccent}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {isCompleted ? (
                    <Check size={20} color="#FFF" />
                  ) : (
                    <Icon size={18} color={isActive ? (isDark ? '#1A0F0A' : '#FFFFFF') : colors.textMuted} />
                  )}
                </div>
                <span style={{
                  fontSize: '10px',
                  color: isCompleted || isActive ? colors.textAccent : colors.inactiveText,
                  fontWeight: isActive ? '600' : '400'
                }}>
                  {s.label}
                </span>
              </div>
              {idx < stepConfig.length - 1 && (
                <div style={{
                  width: '30px',
                  height: '2px',
                  background: stepConfig[idx].completed 
                    ? '#4CAF50'
                    : isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(139, 105, 20, 0.15)',
                  alignSelf: 'flex-start',
                  marginTop: '20px'
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <div style={{ padding: '24px 20px' }}>
        {/* Email Verification - Step 1 */}
        {step === 1 && (
          <div>
            <div style={{
              background: colors.cardBg,
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <Mail size={24} color={colors.textAccent} />
                <div>
                  <h2 style={{
                    color: colors.text,
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: 0
                  }}>
                    Verify Your Email
                  </h2>
                  <p style={{ color: colors.textMuted, fontSize: '13px', margin: '4px 0 0 0' }}>
                    We'll send a 6-digit code to your email
                  </p>
                </div>
              </div>

              {!codeSent ? (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    data-testid="email-input"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.inputBg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.text,
                      fontSize: '16px',
                      marginBottom: '16px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleSendEmailCode}
                    disabled={submitting}
                    data-testid="send-email-code-btn"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.goldGradient,
                      border: 'none',
                      borderRadius: '12px',
                      color: isDark ? '#1A0F0A' : '#FFFFFF',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Sending...' : 'Send Verification Code'}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ color: colors.textMuted, fontSize: '14px', marginBottom: '16px' }}>
                    Code sent to <strong style={{ color: colors.text }}>{email}</strong>
                  </p>
                  
                  {devCode && (
                    <div style={{
                      background: 'rgba(74, 222, 128, 0.1)',
                      border: '1px solid rgba(74, 222, 128, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px'
                    }}>
                      <span style={{ color: '#4ADE80', fontSize: '13px' }}>
                        Test Mode Code: <strong>{devCode}</strong>
                      </span>
                    </div>
                  )}
                  
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    data-testid="verification-code-input"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.inputBg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.text,
                      fontSize: '20px',
                      textAlign: 'center',
                      letterSpacing: '8px',
                      marginBottom: '16px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleVerifyCode}
                    disabled={submitting || verificationCode.length !== 6}
                    data-testid="verify-email-btn"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: verificationCode.length === 6 
                        ? colors.goldGradient
                        : colors.inactiveBg,
                      border: 'none',
                      borderRadius: '12px',
                      color: verificationCode.length === 6 ? (isDark ? '#1A0F0A' : '#FFFFFF') : colors.inactiveText,
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: verificationCode.length === 6 ? 'pointer' : 'not-allowed',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Verifying...' : 'Verify Email'}
                  </button>
                  <button
                    onClick={() => { setCodeSent(false); setVerificationCode(''); setDevCode(''); }}
                    style={{
                      width: '100%',
                      marginTop: '12px',
                      padding: '12px',
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Use different email
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Phone Verification - Step 2 */}
        {step === 2 && (
          <div>
            <div style={{
              background: colors.cardBg,
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <Phone size={24} color={colors.textAccent} />
                <div>
                  <h2 style={{
                    color: colors.text,
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: 0
                  }}>
                    Verify Your Phone
                  </h2>
                  <p style={{ color: colors.textMuted, fontSize: '13px', margin: '4px 0 0 0' }}>
                    We'll send a 6-digit code via SMS
                  </p>
                </div>
              </div>

              {!codeSent ? (
                <>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ''))}
                    placeholder="+1 (555) 123-4567"
                    data-testid="phone-input"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.inputBg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.text,
                      fontSize: '16px',
                      marginBottom: '16px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleSendPhoneCode}
                    disabled={submitting}
                    data-testid="send-phone-code-btn"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.goldGradient,
                      border: 'none',
                      borderRadius: '12px',
                      color: isDark ? '#1A0F0A' : '#FFFFFF',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Sending...' : 'Send Verification Code'}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ color: colors.textMuted, fontSize: '14px', marginBottom: '16px' }}>
                    Code sent to <strong style={{ color: colors.text }}>{phone}</strong>
                  </p>
                  
                  {devCode && (
                    <div style={{
                      background: 'rgba(74, 222, 128, 0.1)',
                      border: '1px solid rgba(74, 222, 128, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px'
                    }}>
                      <span style={{ color: '#4ADE80', fontSize: '13px' }}>
                        Test Mode Code: <strong>{devCode}</strong>
                      </span>
                    </div>
                  )}
                  
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    data-testid="phone-verification-code-input"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.inputBg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.text,
                      fontSize: '20px',
                      textAlign: 'center',
                      letterSpacing: '8px',
                      marginBottom: '16px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleVerifyCode}
                    disabled={submitting || verificationCode.length !== 6}
                    data-testid="verify-phone-btn"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: verificationCode.length === 6 
                        ? colors.goldGradient
                        : colors.inactiveBg,
                      border: 'none',
                      borderRadius: '12px',
                      color: verificationCode.length === 6 ? (isDark ? '#1A0F0A' : '#FFFFFF') : colors.inactiveText,
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: verificationCode.length === 6 ? 'pointer' : 'not-allowed',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Verifying...' : 'Verify Phone'}
                  </button>
                  <button
                    onClick={() => { setCodeSent(false); setVerificationCode(''); setDevCode(''); }}
                    style={{
                      width: '100%',
                      marginTop: '12px',
                      padding: '12px',
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Use different phone
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ID Verification - Step 3 */}
        {step === 3 && (
          <div>
            <div style={{
              background: colors.cardBg,
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <FileText size={24} color={colors.textAccent} />
                <div>
                  <h2 style={{
                    color: colors.text,
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: 0
                  }}>
                    Verify Your Identity
                  </h2>
                  <p style={{ color: colors.textMuted, fontSize: '13px', margin: '4px 0 0 0' }}>
                    Government-issued ID required
                  </p>
                </div>
              </div>

              {/* Age Notice */}
              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <AlertTriangle size={20} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ color: '#F59E0B', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                      Age Verification Notice
                    </div>
                    <p style={{ color: isDark ? '#B8A589' : '#6D6358', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                      You must be <strong>18 years or older</strong> to join NearU. 
                      Your date of birth will be verified from your government ID.
                      Users under 18 will not be able to create an account.
                    </p>
                  </div>
                </div>
              </div>

              {/* Attempts Warning */}
              {verificationAttempts > 0 && attemptsRemaining > 0 && (
                <div style={{
                  background: attemptsRemaining === 1 
                    ? 'rgba(239, 68, 68, 0.1)' 
                    : 'rgba(245, 158, 11, 0.1)',
                  border: `1px solid ${attemptsRemaining === 1 
                    ? 'rgba(239, 68, 68, 0.3)' 
                    : 'rgba(245, 158, 11, 0.3)'}`,
                  borderRadius: '12px',
                  padding: '14px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <AlertCircle 
                    size={18} 
                    color={attemptsRemaining === 1 ? '#EF4444' : '#F59E0B'} 
                  />
                  <div>
                    <span style={{ 
                      color: attemptsRemaining === 1 ? '#EF4444' : '#F59E0B', 
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {attemptsRemaining === 1 
                        ? 'Final Attempt: ' 
                        : `${attemptsRemaining} attempts remaining. `}
                    </span>
                    <span style={{ color: colors.textMuted, fontSize: '13px' }}>
                      {attemptsRemaining === 1 
                        ? 'Please ensure your ID is clear and matches your face.'
                        : 'Make sure to use a valid government ID with a clear photo.'}
                    </span>
                  </div>
                </div>
              )}

              <div style={{
                background: colors.infoBg,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: colors.text, fontSize: '14px', marginBottom: '12px' }}>
                  What you'll need:
                </h3>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: colors.textMuted, fontSize: '13px' }}>
                    <Check size={16} color="#4CAF50" />
                    Driver's license, passport, or government ID
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: colors.textMuted, fontSize: '13px' }}>
                    <Check size={16} color="#4CAF50" />
                    Camera access for live photo capture
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: colors.textMuted, fontSize: '13px' }}>
                    <Check size={16} color="#4CAF50" />
                    Selfie for identity matching
                  </li>
                </ul>
              </div>

              {status?.id_verification?.status === 'processing' && (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Loader2 size={20} color="#8B5CF6" className="animate-spin" />
                  <div>
                    <div style={{ color: '#8B5CF6', fontSize: '14px', fontWeight: '600' }}>
                      Verification in progress
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '12px' }}>
                      This usually takes a few minutes
                    </div>
                  </div>
                </div>
              )}

              {status?.id_verification?.status === 'requires_input' && (
                <div style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <AlertCircle size={20} color="#F59E0B" />
                  <div>
                    <div style={{ color: '#F59E0B', fontSize: '14px', fontWeight: '600' }}>
                      Additional information needed
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '12px' }}>
                      {status?.id_verification?.last_error || 'Please try again with a clearer photo'}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleStartIdVerification}
                disabled={submitting || status?.id_verification?.status === 'processing'}
                data-testid="start-id-verification-btn"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: status?.id_verification?.status === 'processing'
                    ? 'rgba(139, 92, 246, 0.3)'
                    : colors.goldGradient,
                  border: 'none',
                  borderRadius: '12px',
                  color: status?.id_verification?.status === 'processing' ? '#8B5CF6' : (isDark ? '#1A0F0A' : '#FFFFFF'),
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: status?.id_verification?.status === 'processing' ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: submitting ? 0.7 : 1
                }}
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : status?.id_verification?.status === 'processing' ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Processing...
                  </>
                ) : status?.id_verification?.status === 'requires_input' ? (
                  <>
                    <RefreshCw size={18} />
                    Try Again
                  </>
                ) : (
                  <>
                    <ExternalLink size={18} />
                    Start ID Verification
                  </>
                )}
              </button>
            </div>

            {/* Security Notice */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '16px',
              background: 'rgba(16, 185, 129, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <Shield size={20} color="#10B981" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ color: '#10B981', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                  Your data is secure
                </div>
                <div style={{ color: colors.textMuted, fontSize: '12px', lineHeight: '1.5' }}>
                  Verification is powered by Stripe Identity. Your documents are encrypted 
                  and processed securely. We don't store your ID images.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legal Disclosures - Step 4 */}
        {step === 4 && (
          <LegalDisclosuresStep 
            userId={userId}
            isUnder21={isUnder21}
            age={age}
            onComplete={handleLegalDisclosuresComplete}
          />
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            marginTop: '16px',
            padding: '14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <AlertCircle size={18} color="#EF4444" />
            <span style={{ color: '#EF4444', fontSize: '14px' }}>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationFlowPage;
